/**
 * The apps of the dashboard as the sidebar and the Teams page show them: each
 * app's name and summary in the dashboard's language, its icon, and the
 * unread badge. The backend's registry holds the English text; an app the
 * dashboard has no text for yet (a new one) keeps it.
 */

import { ChartColumn, Gauge, LayoutGrid, type LucideIcon } from "lucide-react";

import { formatNumber } from "@/lib/format";
import { i18n } from "@/lib/i18n";
import type { nl } from "@/lib/i18n/locales/nl";

/** The slugs the dashboard has its own text for, under `apps.<slug>`. */
type TranslatedSlug = {
  [K in keyof typeof nl.apps]: (typeof nl.apps)[K] extends { title: string; summary: string }
    ? K
    : never;
}[keyof typeof nl.apps];

const TRANSLATED: ReadonlySet<string> = new Set<TranslatedSlug>(["bezetting", "maandrapport"]);

function isTranslated(slug: string): slug is TranslatedSlug {
  return TRANSLATED.has(slug);
}

interface AppText {
  title: string;
  summary: string;
}

/** An app's name and summary in the dashboard's language, else the backend's own text. */
export function appText(app: { slug: string } & AppText): AppText {
  if (!isTranslated(app.slug)) {
    return { title: app.title, summary: app.summary };
  }
  return { title: i18n.t(`apps.${app.slug}.title`), summary: i18n.t(`apps.${app.slug}.summary`) };
}

/** The registry's lucide icon names the dashboard knows. */
const ICONS: Readonly<Record<string, LucideIcon>> = {
  gauge: Gauge,
  "chart-column": ChartColumn,
};

/** An app's icon by its lucide name; an unknown name gets a neutral grid. */
export function appIcon(name: string): LucideIcon {
  return (Object.hasOwn(ICONS, name) ? ICONS[name] : undefined) ?? LayoutGrid;
}

/** The most a badge counts; above it reads "9+". */
const BADGE_MAX = 9;

/** An unread count as its badge: null at 0 or unknown, "3", or "9+" above 9. */
export function unreadBadge(unread: number | null | undefined): string | null {
  if (!unread || unread <= 0) {
    return null;
  }
  return unread > BADGE_MAX ? `${formatNumber(BADGE_MAX)}+` : formatNumber(unread);
}
